import cx from 'classnames'
import {
  type Ref,
  type AriaAttributes,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react'
import type {
  FormControlProps,
  KeyboardEventsProps,
  MouseEventsProps,
} from '@/infra/prop-types'
import {
  InputShellControl,
  InputShellRoot,
  InputShellSlot,
} from '@/components/inputs/InputShell'
import {
  Select as AriaSelect,
  SelectValue,
  type Selection,
} from 'react-aria-components'
import { useIsSkeleton } from '@/components/Skeleton'
import styles from './Select.module.css'
import { RawButton } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import { SelectOptionsPicker } from './SelectOptionsPicker'
import { isNotNil } from '@/utils/object-util'
import { useElementSize } from '@/hooks/element-hooks'

/**
 * The item key type.
 */
export type Key = string

export interface SelectProps<T extends object>
  extends
    KeyboardEventsProps<HTMLInputElement>,
    MouseEventsProps<HTMLInputElement>,
    FormControlProps,
    AriaAttributes {
  /**
   * Select items.
   */
  items: T[]
  /**
   * Get the key for an item.
   */
  getKey: (item: T) => Key
  /**
   * Get the label for an item.
   */
  getLabel: (item: T) => string
  /**
   * Whether the input is disabled.
   */
  disabled?: boolean
  /**
   * Keys that should be disabled.
   */
  disabledKeys?: Set<Key>
  /**
   * Indicates that the input is required.
   */
  required?: boolean
  /**
   * Whether a skeleton overlay should be rendered.
   * Also disables the input.
   */
  isSkeleton?: boolean
  /**
   * Input placeholder.
   */
  placeholder?: string
  /**
   * Optional ref.
   */
  ref?: Ref<HTMLDivElement>
  /**
   * The current value of the select.
   */
  value?: Key | null
  /**
   * The initial value for the select.
   */
  defaultValue?: Key
  /**
   * Title attribute.
   */
  title?: string
  /**
   * Autocomplete attribute.
   */
  autoComplete?: AutoFill
  /**
   * Change handler.
   *
   * @param key
   */
  onChange?: (key: Key | null) => unknown
  /**
   * Filter function to use in the search field instead of the default.
   */
  filter?: (textValue: string, inputValue: string) => boolean
  /**
   * Whether the select is searchable.
   */
  isSearchable?: boolean
  /**
   * Test ID attribute prefix for the select's elements.
   *
   * The select is a complex component but works more or less
   * the same way for every consumer. The test author is likely to wrap it
   * in a reusable module that just needs a prefix to target a specific
   * instance of the select.
   */
  testId?: string
}

/**
 * Text input root element.
 */
export function Select<T extends object>({
  ref,
  invalid,
  disabled,
  isSkeleton,
  value,
  defaultValue,
  onChange,
  items,
  getKey,
  getLabel,
  disabledKeys,
  filter,
  isSearchable,
  testId,
  ...rest
}: SelectProps<T>) {
  isSkeleton = useIsSkeleton(isSkeleton)
  disabled ||= isSkeleton

  // If not explicitly set, only show the search field if there are
  // 12 or more items.
  isSearchable ??= items.length >= 12

  const [inputValue, setInputValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const { size, connectRef } = useElementSize()
  const rootRef = useRef<HTMLDivElement>(null)
  const mergedRef = useComposedRefs(rootRef, ref, connectRef)

  const selectedKeys = useMemo(
    () => (isNotNil(value) ? new Set<string>([value]) : new Set<string>()),
    [value],
  )

  const [isFocused, setFocused] = useState(false)
  const change = useCallback(
    (value: string | number | null) => {
      if (value === null) {
        onChange?.(null)
      } else {
        onChange?.(typeof value === 'string' ? value : value.toString())
      }

      setInputValue('')
    },
    [onChange],
  )

  const handleSelectionChange = useCallback(
    (selection: Selection) => {
      if (selection === 'all') {
        onChange?.(getKey(items[0]))
      } else {
        onChange?.(selection.entries().next().toString())
      }
    },
    [onChange, items, getKey],
  )

  return (
    <AriaSelect
      value={value}
      defaultValue={defaultValue}
      onFocusChange={setFocused}
      ref={mergedRef}
      isDisabled={disabled}
      onChange={onChange ? change : undefined}
      placeholder="Select"
      onOpenChange={setIsOpen}
      isOpen={isOpen}
      disabledKeys={disabledKeys}
      {...rest}
    >
      <RawButton
        className={styles.button}
        testId={testId ? `${testId}-trigger-button` : undefined}
      >
        <InputShellRoot
          invalid={invalid}
          isDisabled={disabled}
          isFocused={isFocused}
          isSkeleton={isSkeleton}
        >
          <InputShellControl pad>
            <SelectValue>
              {({ defaultChildren, isPlaceholder }) => (
                <span
                  className={cx(
                    styles.value,
                    isPlaceholder && styles.placeholder,
                  )}
                  data-test={testId ? `${testId}-value` : undefined}
                >
                  {defaultChildren}
                </span>
              )}
            </SelectValue>
          </InputShellControl>
          <InputShellSlot side="right">
            <Icon name="chevron" rotate={90} variant="inherit" />
          </InputShellSlot>
        </InputShellRoot>
      </RawButton>

      <SelectOptionsPicker<T>
        aria-label={rest['aria-label']}
        aria-labelledby={rest['aria-labelledby']}
        items={items}
        getKey={getKey}
        getLabel={getLabel}
        isMultiSelect={false}
        isOpen={isOpen}
        selectedKeys={selectedKeys}
        isSearchable={isSearchable}
        // Always use a popover for the select
        // since modal dialogs don't work.
        // https://github.com/adobe/react-spectrum/issues/6021
        as="popover"
        onSelectionChange={handleSelectionChange}
        onOpenChange={setIsOpen}
        searchInputValue={inputValue}
        onSearchInputValueChange={setInputValue}
        triggerRef={rootRef}
        triggerWidth={size.width}
        filter={filter}
        searchInputTestId={testId ? `${testId}-search-input` : undefined}
        clearSearchButtonTestId={
          testId ? `${testId}-clear-search-button` : undefined
        }
        closeMenuButtonTestId={
          testId ? `${testId}-close-menu-button` : undefined
        }
        itemTestId={testId ? `${testId}-option-[key]` : undefined}
      />
    </AriaSelect>
  )
}
